import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Tools|Corrupt Exams';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.BulkCandidateTools,
	portal: 'SP',
};

const htmlContent = `
<p>
	Follow the instructions below to format a CSV file that will allow you to corrupt exams in bulk.
	Select the CSV file then press the Submit button to receive a CSV file with exam and status
	information for each exam registration number.
</p>
<p>Note: There is a 500 row limit.</p>
<p><strong>To create the Comma Separated File (CSV) import file:</strong></p>
<p>
	Include the following column headers recognized by the parser. All fields are required for a
	successful upload. Extra fields will be ignored. Omitting the required columns will result in an
	error.
</p>
<ul>
	<li>Exam Registration Number</li>
	<li>Note</li>
</ul>
<p><strong>Return CSV file:</strong></p>
<p>After pressing the Submit button, a CSV file will be returned with following columns:</p>
<ul>
	<li>Exam Registration Number</li>
	<li>Note</li>
	<li>Update Status</li>
</ul>
<p>The status field will contain one of these values:</p>
<ul>
	<li>Corrupted</li>
	<li>Not Found - Registration Number provided was not found.</li>
</ul>
`;

export type SPExamCorruptionLocNames =
	| 'SPExamCorruptionPageTitle'
	| 'SPExamCorruptionPageHeading'
	| 'SPExamCorruptionButton'
	| 'SPExamCorruptionInstructions'
	| 'SPExamCorruptionDataProviderLabel'
	| 'SPExamCorruptionDataProviderRequired'
	| 'SPExamCorruptionMaxRowsError'
	| 'SPExamCorruptionCurrentImports'
	| 'SPExamCorruptionStatusProcessing'
	| 'SPExamCorruptionStatusCompleted'
	| 'SPExamCorruptionStatusFailed'
	| 'SPExamCorruptionUploadQueuedSuccess'
	| 'SPExamCorruptionNoResults'
	| 'SPExamCorruptionDownloadError'
	| 'SPExamCorruptionDownloadTooltip'
	| 'SPExamCorruptionColumnFileName'
	| 'SPExamCorruptionColumnStatus'
	| 'SPExamCorruptionColumnDate';

export const spExamCorruptionLocs: LocDefs<SPExamCorruptionLocNames> = {
	SPExamCorruptionPageTitle: {
		defaultValue: 'Exam Corruption',
		description: 'Page title for the exam corruption page',
		displayPath: `${dpRoot}|Page Title`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPExamCorruptionPageHeading: {
		defaultValue: 'Candidate Tools',
		description: 'Page Heading for the exam corruption page',
		displayPath: `${dpRoot}|Page Heading`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPExamCorruptionInstructions: {
		defaultValue: htmlContent,
		description: 'Instructions for the exam corruption page',
		displayPath: `${dpRoot}|Instructions`,
		displayType: 'Content',
		...sharedProperties,
		htmlAllowed: true,
	},
	SPExamCorruptionButton: {
		defaultValue: 'Corrupt Exams',
		description: 'Button to start corrupting exams',
		displayPath: `${dpRoot}|Corrupt Exams Button`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPExamCorruptionDataProviderLabel: {
		defaultValue: 'Delivery Provider',
		description: 'Delivery Provider label for the exam corruption page',
		displayPath: `${dpRoot}|Delivery Provider`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPExamCorruptionDataProviderRequired: {
		defaultValue: 'Delivery Provider is required.',
		description: 'Error shown when delivery provider is not selected',
		displayPath: `${dpRoot}|Delivery Provider Required`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPExamCorruptionMaxRowsError: {
		defaultValue: 'You can corrupt 500 exams at the most at a time.',
		description: 'Error shown when more than 500 rows are uploaded for corruption',
		displayPath: `${dpRoot}|Max Rows Error`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPExamCorruptionCurrentImports: {
		defaultValue: 'Current Imports',
		description: 'Title for the current imports section',
		displayPath: `${dpRoot}|Current Imports`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPExamCorruptionStatusProcessing: {
		defaultValue: 'Processing',
		description: 'Status label for processing jobs',
		displayPath: `${dpRoot}|Status Processing`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPExamCorruptionStatusCompleted: {
		defaultValue: 'Completed',
		description: 'Status label for completed jobs',
		displayPath: `${dpRoot}|Status Completed`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPExamCorruptionStatusFailed: {
		defaultValue: 'Failed',
		description: 'Status label for failed jobs',
		displayPath: `${dpRoot}|Status Failed`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPExamCorruptionUploadQueuedSuccess: {
		defaultValue: 'Upload queued successfully',
		description: 'Success message when upload is queued',
		displayPath: `${dpRoot}|Upload Queued Success`,
		displayType: 'Content',
		...sharedProperties,
	},
	SPExamCorruptionNoResults: {
		defaultValue: 'No results available for download',
		description: 'Error message when no results are available',
		displayPath: `${dpRoot}|No Results`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPExamCorruptionDownloadError: {
		defaultValue: 'Failed to download results',
		description: 'Error message when download fails',
		displayPath: `${dpRoot}|Download Error`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPExamCorruptionDownloadTooltip: {
		defaultValue: 'Download Results',
		description: 'Tooltip for download button',
		displayPath: `${dpRoot}|Download Tooltip`,
		displayType: 'Content',
		...sharedProperties,
	},
	SPExamCorruptionColumnFileName: {
		defaultValue: 'File Name',
		description: 'Column header for file name',
		displayPath: `${dpRoot}|Column File Name`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPExamCorruptionColumnStatus: {
		defaultValue: 'Status',
		description: 'Column header for status',
		displayPath: `${dpRoot}|Column Status`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPExamCorruptionColumnDate: {
		defaultValue: 'Date',
		description: 'Column header for date',
		displayPath: `${dpRoot}|Column Date`,
		displayType: 'Label',
		...sharedProperties,
	},
};

export const EXAM_CORRUPTION_LOC_KEYS = MapLocKeys<SPExamCorruptionLocNames>(spExamCorruptionLocs);
export type SPExamCorruptionLocType = LocMap<SPExamCorruptionLocNames>;
